/**
 * TopBar — Figma-faithful top navigation bar
 *
 * Layout: OwnYou logo (left) | Page title (center) | TokenCounter (right)
 * No bottom border, transparent/cream background, h-14.
 *
 * @see Figma_assets/iOS/Home@2x.png for visual reference
 */

import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@ownyou/ui-design-system';
import { TokenCounter } from '@/components/shared/TokenCounter';
import { PAGE_TITLES } from './nav-constants';

export interface TopBarProps {
  /** Whether to show the OwnYou logo (false when sidebar has logo on desktop) */
  showLogo?: boolean;
  /** Override the auto-detected page title */
  pageTitle?: string;
  /** Additional CSS class names */
  className?: string;
}

export function TopBar({
  showLogo = true,
  pageTitle,
  className,
}: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const title = pageTitle || PAGE_TITLES[location.pathname] || '';
  const hideTokenCounter = location.pathname === '/welcome' || location.pathname === '/onboarding';

  return (
    <header
      data-testid="topbar"
      className={cn(
        'sticky top-0 z-40',
        'flex items-center justify-between px-4 h-14',
        className,
      )}
      style={{
        background: 'var(--aurora-card)',
        color: 'var(--aurora-ink)',
        borderBottom: '1px solid var(--aurora-ink-line)',
        backdropFilter: 'blur(16px)',
      }}
    >
      {/* Left: Logo */}
      <div className="flex items-center w-20">
        {showLogo && (
          <button
            type="button"
            data-testid="topbar-logo"
            onClick={() => navigate('/')}
            className="flex items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            aria-label="Go to home"
          >
            <img
              src="/assets/ownyou-logo.png"
              alt="OwnYou"
              className="w-9 h-9 rounded-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </button>
        )}
      </div>

      {/* Center: Logo on Home, page title elsewhere */}
      {location.pathname === '/' ? (
        <div className="flex-1 flex items-center justify-center">
          <img
            src="/assets/ownyou-logo.png"
            alt="OwnYou"
            className="h-8 object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      ) : title ? (
        <h1
          data-testid="topbar-title"
          className="text-lg font-semibold text-center flex-1"
          style={{ fontFamily: 'var(--aurora-font)' }}
        >
          {title}
        </h1>
      ) : (
        <div className="flex-1" />
      )}

      {/* Right: Token counter */}
      <div className="flex items-center justify-end w-20">
        {!hideTokenCounter && <TokenCounter />}
      </div>
    </header>
  );
}
